import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Loader2, Coins, TrendingUp, TrendingDown, Equal } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  adminAdjustUserWallet,
  adminWalletForensic,
  ADJUST_REASONS,
} from '../../api/adminWallet';


const OpBtn = ({ value, icon: Icon, label, testId, current, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(value)}
    className={`flex flex-1 flex-col items-center gap-1 rounded-lg border px-3 py-2 text-xs transition-colors ${
      current === value
        ? 'border-amber-500 bg-amber-500/10 gold-text'
        : 'border-border hover:bg-white/5'
    }`}
    data-testid={testId}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

/**
 * AdminWalletAdjustDialog — admin-only manual wallet operation.
 *
 * Props:
 *  - open, onOpenChange: standard Dialog controlled state.
 *  - user: minimal user shape { id, username, wallet_balance }.
 *  - onDone: called after a successful save with the API response
 *    so the parent (e.g. AdminPage user row) can refresh its data.
 *
 * The dialog also embeds a compact forensic panel below the form so
 * the admin can see recent adjustments + reconciliation status at a
 * glance without navigating away.
 */
const AdminWalletAdjustDialog = ({ open, onOpenChange, user, onDone }) => {
  const { toast } = useToast();
  const [operation, setOperation] = useState('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState(ADJUST_REASONS[0].value);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [forensic, setForensic] = useState(null);

  const loadForensic = useCallback(async () => {
    if (!user?.id) return;
    try {
      const f = await adminWalletForensic(user.id);
      setForensic(f);
    } catch (_e) { /* silent — panel is best-effort */ }
  }, [user?.id]);

  useEffect(() => {
    if (open) {
      setOperation('add');
      setAmount('');
      setReason(ADJUST_REASONS[0].value);
      setNote('');
      loadForensic();
    }
  }, [open, loadForensic]);

  if (!user) return null;

  const currentBalance = forensic?.user?.wallet_balance ?? user.wallet_balance ?? 0;
  const num = Number(amount || 0);
  const projected = operation === 'add'
    ? currentBalance + num
    : operation === 'remove'
      ? currentBalance - num
      : num;
  const wouldGoNegative = projected < 0;

  const submit = async () => {
    if (!amount || num < 0) {
      toast({ title: 'Importo non valido', variant: 'destructive' });
      return;
    }
    if (!reason) {
      toast({ title: 'Motivazione obbligatoria', variant: 'destructive' });
      return;
    }
    if (wouldGoNegative) {
      toast({
        title: 'Saldo non può andare sotto zero',
        description: `Saldo attuale ${currentBalance} · richiesto ${projected}`,
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const res = await adminAdjustUserWallet(user.id, {
        operation, amount: num, reason, note,
      });
      toast({
        title: 'Operazione registrata',
        description: `${res.balance_before} → ${res.balance_after} (${res.amount_delta >= 0 ? '+' : ''}${res.amount_delta})`,
      });
      await loadForensic();
      onDone?.(res);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || String(e);
      toast({ title: 'Errore', description: detail, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="admin-wallet-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 gold-text" /> Wallet · @{user.username}
          </DialogTitle>
          <DialogDescription>
            Ogni operazione è registrata nel <b>wallet_audit_log</b> e non è cancellabile.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border bg-background/40 p-3 text-sm">
          <div className="flex items-baseline gap-3">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Saldo attuale
            </span>
            <span className="text-lg font-semibold tabular-nums" data-testid="admin-adjust-current-balance">
              {currentBalance.toLocaleString()} Crediti
            </span>
            {forensic && !forensic.reconciled.matches && (
              <span className="ml-auto rounded bg-amber-500/20 px-2 py-0.5 text-[10px] text-amber-200">
                drift {forensic.reconciled.drift >= 0 ? '+' : ''}{forensic.reconciled.drift}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold">Operazione</label>
            <div className="flex gap-2">
              <OpBtn value="add" icon={TrendingUp} label="Aggiungi" testId="admin-adjust-op-add" current={operation} onSelect={setOperation} />
              <OpBtn value="remove" icon={TrendingDown} label="Rimuovi" testId="admin-adjust-op-remove" current={operation} onSelect={setOperation} />
              <OpBtn value="set" icon={Equal} label="Imposta" testId="admin-adjust-op-set" current={operation} onSelect={setOperation} />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold">
              {operation === 'set' ? 'Nuovo saldo (crediti)' : 'Crediti'}
            </label>
            <Input
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={operation === 'set' ? '0' : '100'}
              data-testid="admin-adjust-amount-input"
            />
            {amount && (
              <p className={`mt-1 text-[11px] tabular-nums ${wouldGoNegative ? 'text-red-400' : 'text-muted-foreground'}`}>
                Nuovo saldo previsto: <b>{projected.toLocaleString()}</b> Crediti
                {wouldGoNegative && ' — vietato (saldo negativo)'}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold">Motivazione (obbligatoria)</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="admin-adjust-reason-select"
            >
              {ADJUST_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold">Note (opzionali)</label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="es. ticket #423, ordine EUR-2026-091, ..."
              maxLength={500}
              data-testid="admin-adjust-note-input"
            />
          </div>
        </div>

        {forensic && forensic.admin_adjustments?.length > 0 && (
          <div className="rounded-lg border border-border p-3">
            <p className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              Ultime {Math.min(5, forensic.admin_adjustments.length)} modifiche manuali
            </p>
            <ul className="space-y-1 text-xs">
              {forensic.admin_adjustments.slice(0, 5).map((a) => (
                <li key={a.id} className="flex items-baseline justify-between gap-2">
                  <span className="text-muted-foreground">
                    {new Date(a.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className={`font-mono tabular-nums ${a.amount_delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {a.amount_delta >= 0 ? '+' : ''}{a.amount_delta}
                  </span>
                  <span className="flex-1 truncate">
                    {ADJUST_REASONS.find((r) => r.value === a.reason)?.label || a.reason}
                    {a.note && <span className="text-muted-foreground"> · {a.note}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="admin-adjust-cancel">
            Annulla
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !amount || wouldGoNegative}
            className="gold-gradient text-black"
            data-testid="admin-adjust-submit"
          >
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Applica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminWalletAdjustDialog;
